import React, { useRef } from 'react';
import {
  Box,
  Avatar,
  Typography,
  Button,
  Paper,
  IconButton,
  Stack
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  ContentCopy as CopyIcon,
  CameraAlt as CameraAltIcon,
  OpenInNew as OpenInNewIcon,
  QrCode as QrCodeIcon
} from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from '../../../hooks/useTranslation';

const ProfileHeader = ({
  userProfile,
  isMobile,
  copied,
  onOpenPublicProfile,
  onCopyToClipboard,
  onUpdatePhoto,
  onQRCodeClick
}) => {
  const fileInputRef = useRef(null);
  const { t } = useTranslation();

  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      alert(t('dashboard.onlyJpegPngAccepted'));
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 200;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 200, 200);
        canvas.toBlob((blob) => {
          if (onUpdatePhoto) {
            onUpdatePhoto(blob);
          }
        }, file.type);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const avatarProps = {
    sx: {
      width: isMobile ? 50 : 48,
      height: isMobile ? 50 : 48,
      cursor: 'pointer',
      borderRadius: '50%',
      border: '1px solid',
      borderColor: 'grey.300',
      bgcolor: 'grey.200',
      transition: 'background-color 0.2s ease',
      '&:hover': { bgcolor: 'grey.300' }
    },
    onClick: handleAvatarClick,
    title: 'Cliquer pour changer la photo'
  };

  const renderAvatar = () => {
    if (userProfile.photo) {
      return (
        <Avatar
          {...avatarProps}
          src={userProfile.photo}
        />
      );
    }
    return (
      <Avatar
        {...avatarProps}
      >
        <CameraAltIcon sx={{ color: 'grey.700' }} />
      </Avatar>
    );
  };

  const editIconButton = (
    <IconButton 
      sx={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        backgroundColor: 'grey.300',
        p: 0.5,
        borderRadius: '50%',
        transition: 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: 'grey.500'
        }
      }}
      onClick={handleAvatarClick}
      size="small"
      title="Cliquer pour changer la photo"
    >
      <EditIcon fontSize="small" sx={{ color: 'grey.700' }} />
    </IconButton>
  );

  const contentComponent = isMobile ? (
    <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.main', color: 'white' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          {renderAvatar()}
          {onUpdatePhoto && editIconButton}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {userProfile.salonName || 'Mon Salon'}
          </Typography>
        </Box>
      </Box>
      <Stack direction="row" spacing={1}>
        <IconButton
          onClick={onOpenPublicProfile}
          sx={{
            flex: 1,
            borderRadius: '50px',
            border: '1px solid rgba(255,255,255,0.3)',
            color: 'white',
            '&:hover': {
              borderColor: 'rgba(255,255,255,0.5)',
              bgcolor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          <OpenInNewIcon />
        </IconButton>
        <IconButton
          onClick={onQRCodeClick}
          sx={{
            flex: 1,
            borderRadius: '50px',
            border: '1px solid rgba(255,255,255,0.3)',
            color: 'white',
            '&:hover': {
              borderColor: 'rgba(255,255,255,0.5)',
              bgcolor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          <QrCodeIcon />
        </IconButton>
        <IconButton
          onClick={onCopyToClipboard}
          sx={{
            flex: 1,
            borderRadius: '50px',
            bgcolor: 'rgba(255,255,255,0.2)',
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.3)'
            }
          }}
        >
          <CopyIcon />
        </IconButton>
      </Stack>
    </Paper>
  ) : (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          {renderAvatar()}
          {onUpdatePhoto && editIconButton}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {userProfile.salonName || 'Nom du salon'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={onOpenPublicProfile}
            sx={{
              borderColor: '#d6d3d1',
              color: '#57534e',
              borderRadius: '50px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#a8a29e',
                bgcolor: '#f5f5f4'
              }
            }}
          >
            Voir ma fiche
          </Button>
          <Button
            variant="outlined"
            startIcon={<QrCodeIcon />}
            onClick={onQRCodeClick}
            sx={{
              borderColor: '#6B46C1',
              color: '#6B46C1',
              borderRadius: '50px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#553C9A',
                bgcolor: '#F3F4F6'
              }
            }}
          >
            QR Code
          </Button>
          <Button
            variant="contained"
            startIcon={<CopyIcon />}
            onClick={onCopyToClipboard}
            sx={{
              bgcolor: '#1c1917',
              borderRadius: '50px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#292524' }
            }}
          >
            {copied ? 'Copié!' : 'Copier le lien'}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );

  return (
    <>
      {contentComponent}
      <input 
        type="file" 
        accept="image/jpeg,image/png" 
        ref={fileInputRef} 
        style={{ display: 'none' }} 
        onChange={handleFileChange} 
      />
    </>
  );
};

export default ProfileHeader;
